import React from "react";
import ArticleHeader from "../../../../components/articles/ArticleHeader";
import ArticleShareBottom from "../../../../components/articles/ArticleShareBottom";
import ArticleFooter from "../../../../components/articles/ArticleFooter";
import ArticleRecommendation from "../../../../components/articles/ArticleRecommendation";
import InfoArticleStyles from "../../../../styles/InfoArticleStyles";
import Seo from "../../../../seo";
import { StaticImage } from "gatsby-plugin-image";

const RestaurantsNeedSustainabilityHelp = ({ location }) => {
  const data = {
    title:
      "Restaurants Need Support to Continue Sustainability Efforts During COVID-19",
    date: "January 25, 2021",
    subHeader:
      "Multi-stakeholder collaboration can help mitigate the impact of disposable food packaging during the pandemic.",
    author: "Nyasha Mugavazi",
    coAuthors: "Co-authored by Cathy Sorbara and Serena Weaver",
    nextArticle: {
      header: "See recommendations for Amsterdam eateries using eco-packaging.",
      id: "6143ba66f34f50066f484e50",
    },
  };
  return (
    <>
      <Seo
        title="Restaurants Need Support to Continue Sustainability Efforts During COVID-19"
        description="Multi-stakeholder collaboration can help mitigate the impact of disposable food packaging during the pandemic."
        url="https://www.table-sage.com/content/articles/amsterdam/restaurants-need-support-to-continue-sustainability-efforts-during-covid-19"
        image="https://res.cloudinary.com/table-sage/image/upload/v1611571483/articles/amsterdam/azhar-j-t2hgHV1R7_g-unsplash_1_ttgykx.jpg"
        imageWidth="500"
        imageHeight="334"
      />
      <ArticleHeader data={data} currentUrl={location.pathname} />
      <InfoArticleStyles>
        <div className="info-article__container">
          <StaticImage
            alt="Beautiful canal in Amsterdam seems stormy as city restaurants work through pandemic changes. Article by Table Sage team"
            src="https://res.cloudinary.com/table-sage/image/upload/v1611571483/articles/amsterdam/azhar-j-t2hgHV1R7_g-unsplash_1_ttgykx.jpg"
            placeholder="blurred"
            layout="constrained"
            quality={80}
            className="info-article__photo"
            width={1000}
          />
          <p className="info-article__photo-caption">
            75% of Amsterdam restaurants responding to a 2020 survey conducted
            by Table Sage believe the government should be more supportive of
            sustainability efforts in the local dining industry.
          </p>
          <p className="info-article__body-copy">
            The dining industry has demonstrated an increasing commitment to
            sustainability. All over the world, independent eateries in
            particular have led the way as the industry takes accountability for
            its impact on our environment and our communities. The COVID-19
            pandemic has posed a serious threat to this trend, albeit one that
            many are committed to mitigating. Eateries are now faced with the
            challenges of keeping as many people as possible employed; keeping
            their clients, staff, and suppliers physically safe; and adapting
            measures to make their businesses resilient. With the pandemic’s end
            uncertain and lockdowns continuously unpredictable, this is no easy
            feat.
          </p>
          <p className="info-article__body-copy">
            In Autumn 2020, Table Sage conducted a survey of restaurants in
            Amsterdam aimed at understanding the impact of COVID-19 on local
            eatery sustainability in order to identify avenues for
            multi-stakeholder support. For starters, the survey found that 58%
            of respondents had begun offering takeaway directly in response to
            the pandemic. While this has the potential to allow businesses to
            continue to operate safely, takeaway also comes with its own
            challenges surrounding sustainability and packaging.
          </p>
          <p className="pull-quote">
            “The high cost of eco-packaging and the difficulty of sourcing
            alternatives can be existential ... many businesses just don’t know
            where to begin”
          </p>
          <p className="info-article__body-copy">
            With indoor and in some cases all seated dining banned in major
            cities all over the world, the specter of takeaway packaging looms
            over us more heavily than ever before. Amongst the difficulties that
            are increasingly urgent for restaurants is the sourcing of
            ecological packaging for this service. Although this may not be an
            obstacle for larger industry players with brand visibility, the
            Table Sage survey showed that for the independent eateries that are
            often trailblazers in conscious dining and the backbone of local
            economies, the high cost of eco-packaging and the barriers to
            sourcing alternatives can be existential. In addition to this, many
            businesses just don’t know where to begin.
          </p>
          <p className="pull-quote">
            “67% of respondents [recognize] eco-packaging as core to their
            business principles and 82% ... [have] chosen an eco-packaging
            option”
          </p>
          <p className="info-article__body-copy">
            Still, we have reason to be hopeful. While the challenges to
            sustainable dining multiply, commitment to conscious dining
            continues to grow with even major players such as UberEATS taking
            note of the importance of sustainable packaging to its users, within
            its rating system. In Amsterdam, respondents to the Table Sage
            survey voiced both a concern for the impact of the dining industry
            on the environment and a desire for us to collectively do better.
            Their words matched their actions, with 67% of respondents
            recognizing eco-packaging as core to their business principles and
            82% of respondents having found and chosen an eco-packaging option
            based on their ethics.
          </p>
          <p className="info-article__body-copy">
            Despite the continued commitment that eateries have demonstrated
            towards operating according to the principles of conscious dining,
            the fact remains that surviving sustainably during a pandemic is
            difficult. An online search of the word “sustainability” today comes
            up with a wealth of information. According to one of the survey's
            respondents, an additional problem is that there is too much
            information, and much of it is seemingly contradictory. It is
            difficult for eateries to know what to prioritize because in
            reality, sustainability means something entirely different for an
            individual in Izmir and for a cafe in Copenhagen, or even for a raw
            vegan restaurant in Amsterdam and a restaurant serving the exact
            same menu in Kinshasa. There is a clear need for information to be
            tailored to different stakeholders in different contexts, a
            principle which applies beyond the city of Amsterdam and beyond the
            dining industry.
          </p>
          <p className="pull-quote">
            “As the pandemic has continued ... government efforts have slowed
            and many eateries are struggling to both remain open and remain
            sustainable”
          </p>
          <p className="info-article__body-copy">
            Government responses to the pandemic’s impact on the dining industry
            have been varied across the world, ranging from local grant programs
            to government subsidized restaurant meals to no response at all. In
            many cases, as the pandemic has continued to grind on, government
            efforts have slowed and many eateries are struggling to both remain
            open and remain sustainable without this much needed support.
            Eateries that are trying to do right by their communities and for
            the world should not be left at a loss for support, and yet many
            eateries feel abandoned by their local governments. In fact, 75% of
            respondents to the Table Sage survey hold the sentiment that
            government bodies should do more to support restaurants in operating
            sustainably. The loss of government interest in the dining industry
            is a trend that urgently needs to be reversed all over the world.
          </p>
          <p className="pull-quote">
            “Netherlands based Ozarka’s reusable food packaging service [shows]
            us that when we work together, waste is a choice and not a given”
          </p>
          <p className="info-article__body-copy">
            Fortunately, there are ways in which both pre-existing and new
            stakeholders can work to identify and fill gaps in sustainability
            efforts at this time. Social enterprises, for example, are providing
            innovative and fast-growing solutions to these challenges. Danish{" "}
            <a
              href="https://toogoodtogo.org/en"
              className="dropdown-learn-more-link"
              rel="noreferrer noopener"
              target="_blank"
            >
              Too Good To Go
            </a>{" "}
            uses a circular understanding of sustainability to decrease
            restaurant food waste through a sophisticated business-to-consumer
            matching model. Meanwhile, Netherlands based{" "}
            <a
              href="https://ozarka.nl/"
              className="dropdown-learn-more-link"
              rel="noreferrer noopener"
              target="_blank"
            >
              Ozarka’s
            </a>{" "}
            reusable food packaging service, due to launch this month, likewise
            highlights that waste is a choice and not a given. These
            alternatives show that there are multiple avenues to take advantage
            of in our continued efforts towards a more sustainable future.
          </p>
          <p className="info-article__body-copy">
            While there is increasing concern surrounding the dining industry's
            impact on people and the planet, opportunities to support
            restaurants' positive efforts exist and must be utilized with
            priority as the pandemic continues.
          </p>
          <ArticleShareBottom data={data} currentUrl={location.pathname} />
          <ArticleRecommendation
            header={data.nextArticle.header}
            id={data.nextArticle.id}
          />
          <ArticleFooter
            buttonText="See more articles"
            buttonUrl="/content/best-of/amsterdam"
          />
        </div>
      </InfoArticleStyles>
    </>
  );
};

export default RestaurantsNeedSustainabilityHelp;
